// ── /api/kids/rejects* — «Просмотр сессий» (реджекты + отгруженные) ──────────
// Раздел кабинета для пересмотра видео УЖЕ отгруженных сессий: вкладка
// «Реджекты» (client_status='rejected') и «Отгруженные» (uploaded_at IS NOT
// NULL — все, с бейджем вердикта клиента; Tim 11.08 — админы делают QA сразу
// после отгрузки, не дожидаясь вердикта). Пути остались /api/kids/rejects —
// старый прод-фронт без `tab` получает прежнее поведение (реджекты).
//
// Скоуп зеркалит dashboard/sessions: manager=все офисы, admin=СВОЙ офис
// (Tim 11.08 — кросс-офисную выдачу реджектов сузили), moderator=только свои.
import { z } from 'zod';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export const KIDS_REVIEW_TABS = ['rejected', 'shipped'] as const;
export type KidsReviewTab = (typeof KIDS_REVIEW_TABS)[number];

export const KidsRejectsReqSchema = z.object({
  tab: z.enum(KIDS_REVIEW_TABS).optional(), // omitted → 'rejected' (старые клиенты)
  moderator: z.string().trim().max(64).optional(), // модератор игнорирует, admin/manager сужают
  from: z.string().regex(DATE_RE).optional(), // окно по started_at, дни включительно
  to: z.string().regex(DATE_RE).optional(),
  // Поиск по идентификаторам: имя участника (подстрока), #id / id сессии,
  // клиентский PID вида P010023 (P<modPid:2><seq:4>).
  q: z.string().trim().max(80).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(), // дефолт/потолок 200
  offset: z.coerce.number().int().min(0).max(100000).optional(),
});
export type KidsRejectsReq = z.infer<typeof KidsRejectsReqSchema>;

export const KidsRejectRowSchema = z.object({
  id: z.number().int(),
  // клиентский PID P<modPid:2><seq:4>; null, когда у модератора сессии нет pid
  pid: z.string().nullable(),
  childName: z.string(),
  childAge: z.number().int().nullable(),
  modId: z.string(),
  modName: z.string(),
  modPid: z.string().nullable(),
  startedAt: z.number().int().nullable(), // epoch ms съёмки
  verdictAt: z.number().int().nullable(), // client_status_at
  uploadedAt: z.number().int().nullable(),
  clientStatus: z.enum(['accepted', 'rejected', 'pending']).nullable(),
  packed: z.boolean(),
});
export type KidsRejectRow = z.infer<typeof KidsRejectRowSchema>;

export const KidsRejectModSchema = z.object({
  id: z.string(),
  name: z.string(),
  pid: z.string().nullable(),
  n: z.number().int().nonnegative(), // сессий текущей вкладки в скоупе
});
export type KidsRejectMod = z.infer<typeof KidsRejectModSchema>;

export const KidsRejectsResSchema = z.object({
  sessions: z.array(KidsRejectRowSchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
  // Справочник модов для фильтра (по вкладке, в пределах скоупа; БЕЗ учёта
  // выбранного мода/поиска — иначе после выбора не к чему вернуться).
  // Для модератора всегда [].
  mods: z.array(KidsRejectModSchema),
});
export type KidsRejectsRes = z.infer<typeof KidsRejectsResSchema>;

// ── GET /api/kids/rejects/clips — какие клипы сессии есть в хранилище ────────
export const KidsRejectClipsReqSchema = z.object({
  sessionId: z.coerce.number().int().positive(),
});
export type KidsRejectClipsReq = z.infer<typeof KidsRejectClipsReqSchema>;

/** Где нашлись клипы: свежие, упакованный архив или ручной брак. */
export const KIDS_CLIP_SOURCES = ['input/', 'archive/', 'брак/'] as const;

export const KidsRejectClipSchema = z.object({
  idx: z.number().int().nonnegative(),
  step: z.number().int().nullable(), // номер шага съёмки из имени файла
  bytes: z.number().int().nonnegative(),
  ticket: z.string(), // подписанный тикет на этот клип (30 мин), для <video src>
});
export type KidsRejectClip = z.infer<typeof KidsRejectClipSchema>;

export const KidsRejectClipsResSchema = z.object({
  sessionId: z.number().int().positive(),
  clips: z.array(KidsRejectClipSchema),
  source: z.enum(KIDS_CLIP_SOURCES).nullable(),
});
export type KidsRejectClipsRes = z.infer<typeof KidsRejectClipsResSchema>;

// ── GET /api/kids/rejects/video — байты клипа из R2 (binary, Range) ──────────
// Ответ — видеопоток, контракт покрывает только query (см. contracts.md).
export const KidsRejectVideoReqSchema = z.object({
  sessionId: z.coerce.number().int().positive(),
  idx: z.coerce.number().int().min(0).max(9),
  t: z.string().max(200).optional(), // тикет; альтернатива — Bearer
});
export type KidsRejectVideoReq = z.infer<typeof KidsRejectVideoReqSchema>;
